import { PATRON_LEVELS, TPatronLevel } from '../../../common/patron';
import { IConnectedAccount, IUnifiedSubscription, TSubscriptionSource } from '../../../common/purchases';

export type TCurrentPremiumSummarySource = TSubscriptionSource | 'multiple' | 'none';
export type TCurrentPremiumSummaryDateKind = 'access_through' | 'current_period_end' | 'none';
export type TSubscriptionActivityKind = 'subscription' | 'unlock' | 'grant';
export type TSubscriptionActivityStatus = 'active' | 'auto_renew_off' | 'canceled' | 'expired' | 'inactive';

export interface ISubscriptionActivityRow {
  id: string;
  subscription: IUnifiedSubscription;
  kind: TSubscriptionActivityKind;
  displaySource: TSubscriptionSource;
  status: TSubscriptionActivityStatus;
  reference: string;
  createdAt: string;
  endsAt: string | null;
}

export type TCurrentPremiumSummary = {
  isActive: boolean;
  source: TCurrentPremiumSummarySource;
  activeSubscriptions: IUnifiedSubscription[];
  primarySubscription: IUnifiedSubscription | null;
  effectiveSource: TSubscriptionSource | null;
  additionalSources: TSubscriptionSource[];
  nextDate: string | null;
  nextDateKind: TCurrentPremiumSummaryDateKind;
};

export function maskEmail(email?: string | null): string | null {
  if (!email) {
    return null;
  }

  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) {
    return null;
  }

  const visibleStart = Math.min(2, localPart.length);
  const visibleEnd = localPart.length > 4 ? 2 : localPart.length > 2 ? 1 : 0;
  const hiddenLength = Math.max(3, localPart.length - visibleStart - visibleEnd);

  return `${localPart.slice(0, visibleStart)}${'*'.repeat(hiddenLength)}${visibleEnd ? localPart.slice(-visibleEnd) : ''}@${domain}`;
}

export function getMaskedAccountIdentity(account?: IConnectedAccount | null): string | null {
  const maskedIdentities = Array.from(
    new Set(
      (account?.identities?.length
        ? account.identities.map((identity) => maskEmail(identity.email))
        : [maskEmail(account?.email)]
      ).filter((value): value is string => !!value)
    )
  );

  if (maskedIdentities.length === 0) {
    return null;
  }

  return maskedIdentities.join(', ');
}

export function getSubscriptionActivityKind(subscription: IUnifiedSubscription): TSubscriptionActivityKind {
  if (subscription.transactionId.startsWith('RIO-UNLOCK-') || subscription.productCode.startsWith('claimed_')) {
    return 'unlock';
  }

  if (subscription.source === 'internal') {
    return 'grant';
  }

  if (getSubscriptionTierSlug(subscription.productCode)) {
    return 'subscription';
  }

  return 'unlock';
}

export function getSubscriptionDisplaySource(subscription: IUnifiedSubscription): TSubscriptionSource {
  return subscription.source;
}

export function getSubscriptionSummarySource(subscription: IUnifiedSubscription): TSubscriptionSource {
  return getSubscriptionActivityKind(subscription) === 'subscription' ? subscription.source : 'internal';
}

export function getSubscriptionActivityStatus(subscription: IUnifiedSubscription): TSubscriptionActivityStatus {
  const endsAtMs = subscription.subscriptionEndsAt ? new Date(subscription.subscriptionEndsAt).getTime() : null;
  const isExpired = endsAtMs !== null && endsAtMs <= Date.now();

  if (isExpired) {
    return 'expired';
  }

  if (subscription.purchaseStatus === 'non_renewing' && subscription.subscriptionEndsAt) {
    return 'auto_renew_off';
  }

  if (
    subscription.disabledAt ||
    subscription.purchaseStatus === 'canceled' ||
    subscription.purchaseStatus === 'refunded'
  ) {
    return 'canceled';
  }

  return subscription.isActive ? 'active' : 'inactive';
}

export function buildSubscriptionActivityRows(subscriptions: IUnifiedSubscription[]): ISubscriptionActivityRow[] {
  return [...subscriptions]
    .filter((subscription) => !shouldHideFromActivity(subscription.productCode))
    .sort((left, right) => {
      const timeDifference = new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
      if (timeDifference !== 0) {
        return timeDifference;
      }

      return right.id - left.id;
    })
    .map((subscription) => ({
      id: String(subscription.id),
      subscription,
      kind: getSubscriptionActivityKind(subscription),
      displaySource: getSubscriptionDisplaySource(subscription),
      status: getSubscriptionActivityStatus(subscription),
      reference: subscription.transactionId,
      createdAt: subscription.createdAt,
      endsAt: subscription.subscriptionEndsAt
    }));
}

export function paginateSubscriptionActivityRows(
  rows: ISubscriptionActivityRow[],
  pageSize: number,
  pageIndex: number
) {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPageIndex = Math.min(Math.max(pageIndex, 0), pageCount - 1);
  const startIndex = currentPageIndex * pageSize;

  return {
    pageCount,
    pageIndex: currentPageIndex,
    rows: rows.slice(startIndex, startIndex + pageSize)
  };
}

export function hasXsollaSubscriptionState(subscriptions: IUnifiedSubscription[]): boolean {
  return subscriptions.some(
    (subscription) => subscription.source === 'xsolla' && getSubscriptionActivityKind(subscription) === 'subscription'
  );
}

const PERMANENT_HORIZON_MS = 10 * 365.25 * 24 * 60 * 60 * 1000;

export function isEffectivelyPermanent(value: string | null | undefined): boolean {
  if (!value) {
    return true;
  }
  const endsAt = new Date(value).getTime();
  if (Number.isNaN(endsAt)) {
    return false;
  }
  return endsAt - Date.now() > PERMANENT_HORIZON_MS;
}

export function isSubscriptionCurrentlyActive(subscription: IUnifiedSubscription): boolean {
  if (!subscription.isActive) {
    return false;
  }

  if (!subscription.subscriptionEndsAt) {
    return true;
  }

  return new Date(subscription.subscriptionEndsAt).getTime() > Date.now();
}

export function getActiveSubscriptionBySource(
  subscriptions: IUnifiedSubscription[],
  source: TSubscriptionSource
): IUnifiedSubscription | null {
  return (
    subscriptions.find(
      (subscription) =>
        getSubscriptionSummarySource(subscription) === source && isSubscriptionCurrentlyActive(subscription)
    ) || null
  );
}

export function getCurrentPremiumSummary(subscriptions: IUnifiedSubscription[]): TCurrentPremiumSummary {
  const activeSubscriptions = subscriptions.filter((subscription) => isSubscriptionCurrentlyActive(subscription));
  if (activeSubscriptions.length === 0) {
    return {
      isActive: false,
      source: 'none',
      activeSubscriptions: [],
      primarySubscription: null,
      effectiveSource: null,
      additionalSources: [],
      nextDate: null,
      nextDateKind: 'none'
    };
  }

  const summarySources = Array.from(
    new Set(activeSubscriptions.map((subscription) => getSubscriptionSummarySource(subscription)))
  );
  const primarySubscription = getPrimarySubscription(activeSubscriptions);
  const effectiveSource = primarySubscription ? getSubscriptionSummarySource(primarySubscription) : null;

  return {
    isActive: true,
    source: summarySources.length > 1 ? 'multiple' : summarySources[0],
    activeSubscriptions,
    primarySubscription,
    effectiveSource,
    additionalSources: effectiveSource ? summarySources.filter((source) => source !== effectiveSource) : summarySources,
    nextDate: primarySubscription?.subscriptionEndsAt || null,
    nextDateKind:
      primarySubscription?.purchaseStatus === 'non_renewing' && primarySubscription.subscriptionEndsAt
        ? 'access_through'
        : primarySubscription?.subscriptionEndsAt
          ? 'current_period_end'
          : 'none'
  };
}

export function getSubscriptionTierSlug(productCode: string): string | null {
  return PATRON_LEVELS[productCode]?.slug || null;
}

export function getCurrentPremiumTier(summary: TCurrentPremiumSummary): TPatronLevel | null {
  if (!summary.primarySubscription) {
    return null;
  }
  return PATRON_LEVELS[summary.primarySubscription.productCode] || null;
}

export function humanizeSubscriptionCode(value: string): string {
  return value
    .replace(/^claimed_/i, '')
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// Internal role-grant product codes that should not appear in the user-facing
// Premium Activity table. Users don't have a mental model for these — they're
// granted internally for staff/test access.
const HIDDEN_ACTIVITY_PRODUCT_CODES = new Set<string>([
  'xsolla_tester',
  'support_team',
  'news_editor',
  'developer',
  'rwf_helper'
]);

export function shouldHideFromActivity(productCode: string): boolean {
  return HIDDEN_ACTIVITY_PRODUCT_CODES.has(productCode);
}

export function getSubscriptionDisplayName(subscription: IUnifiedSubscription): string {
  const { productCode } = subscription;
  const patronLevel = PATRON_LEVELS[productCode];

  if (patronLevel && productCode.startsWith('premium_')) {
    return `${patronLevel.name} Premium`;
  }

  return productCode;
}

function getPrimarySubscription(subscriptions: IUnifiedSubscription[]): IUnifiedSubscription | null {
  if (subscriptions.length === 0) {
    return null;
  }

  // Prefer real Premium subscriptions over unlock/grant kinds so the page header
  // can show the active tier even when permanent unlocks (e.g. claimed_guild,
  // claimed_paladin) carry far-future end dates that would otherwise win the sort.
  const tierSubscriptions = subscriptions.filter(
    (subscription) => getSubscriptionActivityKind(subscription) === 'subscription'
  );
  const candidatePool = tierSubscriptions.length > 0 ? tierSubscriptions : subscriptions;

  const candidatesWithEndDates = candidatePool
    .filter((subscription) => subscription.subscriptionEndsAt)
    .sort(
      (left, right) =>
        new Date(right.subscriptionEndsAt as string).getTime() - new Date(left.subscriptionEndsAt as string).getTime()
    );

  if (candidatesWithEndDates.length > 0) {
    return candidatesWithEndDates[0];
  }

  return [...candidatePool].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  )[0];
}
